import { GoogleGenerativeAI } from "@google/generative-ai";
import { GoogleGenAI } from "@google/genai";
import { VideoLength, StoryLanguage, StoryStructure } from '../types';

export interface StoryShotConfig {
  apiKey: string;
  model?: string;
  videoModel?: string;
}

export interface StoryShotScript {
  content: string;
  scenes: Array<{
    scene: number;
    duration: string;
    visual: string;
    audio: string;
  }>;
}

export class StoryShot {
  private genAI: GoogleGenerativeAI;
  private veoAI: GoogleGenAI | null = null;
  private config: StoryShotConfig;

  constructor(config: StoryShotConfig) {
    if (!config.apiKey) {
      throw new Error("API key is required for StoryShot initialization");
    }
    
    this.config = {
      model: 'gemini-1.5-flash',
      videoModel: 'veo-3.0-generate-preview',
      ...config
    };
    
    this.genAI = new GoogleGenerativeAI(this.config.apiKey);
    
    // Initialize Veo AI client
    try {
      this.veoAI = new GoogleGenAI({
        apiKey: this.config.apiKey
      });
    } catch (error) {
      console.warn("Could not initialize Veo AI client:", error);
    }
  }

  /**
   * Generate a story script for YouTube Shorts with seamless 8-second segments
   */
  async generateScript(
    topic: string, 
    length: VideoLength, 
    structure: StoryStructure = StoryStructure.HOOK_TO_END
  ): Promise<string> {
    const totalSeconds = parseInt(length);
    const numSegments = totalSeconds / 8;
    

    const structureTemplates: Record<StoryStructure, string> = {
      [StoryStructure.HOOK_TO_END]: `
        - Start with an IRRESISTIBLE hook that creates immediate curiosity
        - Build tension progressively through each segment
        - Use cliffhangers at segment transitions to maintain attention
        - End with a satisfying payoff that rewards viewers for watching`,
      [StoryStructure.DRAMATIC_TWIST]: `
        - Set up expectations in the first segment
        - Build believable narrative in middle segments
        - Deliver SHOCKING twist in final moments
        - The twist must completely reframe everything shown before`,
      [StoryStructure.EMOTIONAL_JOURNEY]: `
        - Begin with relatable emotional situation
        - Build empathy and connection progressively
        - Reach emotional climax in final segment
        - Leave viewers with strong feelings (joy, inspiration, or tears)`,
      [StoryStructure.COMEDY_BUILDUP]: `
        - Start with amusing setup
        - Escalate comedy through unexpected developments
        - Build to hilarious climax
        - End with memorable punchline or callback`,
      [StoryStructure.MYSTERY_REVEAL]: `
        - Present intriguing mystery or question immediately
        - Drop hints and clues throughout segments
        - Build suspense and theories
        - Reveal surprising answer that makes perfect sense`,
      [StoryStructure.TUTORIAL_STEPS]: `
        - Clear introduction of what will be learned
        - Step-by-step progression through process
        - Visual demonstrations for each step
        - Final result showcase and recap`,
      [StoryStructure.BEFORE_AFTER]: `
        - Show problematic "before" state clearly
        - Document transformation process
        - Build anticipation for final result
        - Reveal dramatic "after" with impact`,
      [StoryStructure.CHALLENGE_RESULT]: `
        - Present the challenge or goal upfront
        - Show attempts and struggles
        - Build tension about success/failure
        - Reveal final outcome with celebration or lesson`,
      [StoryStructure.FACTS_SURPRISE]: `
        - Start with mind-blowing fact hook
        - Escalate with increasingly surprising information
        - Connect facts in unexpected ways
        - End with most shocking revelation`,
      [StoryStructure.STORY_LOOP]: `
        - Begin with intriguing scene
        - Develop story that seems linear
        - Reveal connection to beginning in final moment
        - Create "aha" moment where everything clicks`,
    };
    
    const prompt = `
      You are an expert scriptwriter specializing in viral YouTube Shorts.
      Your task is to generate a COMPLETE story script that will be divided into ${numSegments} seamless 8-second segments.

      **Video Topic:** "${topic}"
      **Total Story Length:** ${totalSeconds} seconds (${numSegments} segments of 8 seconds each)
      **Language:** English - ALL dialogue, narration, and text must be in English
      
      **STORY STRUCTURE REQUIREMENTS:**
      ${structureTemplates[structure]}

      **CRITICAL REQUIREMENTS:**
      1. Create a COMPLETE story with beginning, middle, and end within ${totalSeconds} seconds
      2. The story must be divided into exactly ${numSegments} segments, each EXACTLY 8 seconds
      3. Each segment must END with a smooth transition that naturally leads to the next segment
      4. The segments when played sequentially must form a seamless, coherent story
      5. NO cliffhangers between segments - smooth narrative flow is essential
      
      **CHARACTER & SCENE CONTINUITY REQUIREMENTS:**
      1. **Character Consistency**: 
         - Use the SAME main character(s) throughout ALL segments
         - Maintain consistent character names, appearances, and personalities
         - Character development should be gradual and logical across segments
         
      2. **Scene Continuity**:
         - Each scene should logically flow from the previous one
         - Maintain consistent settings or show clear transitions between locations
         - Visual elements (props, backgrounds) should remain consistent or change logically
         
      3. **Narrative Thread**:
         - Single, continuous storyline across all segments
         - Each segment builds upon the previous one
         - No sudden jumps or disconnected scenes
      
      **Story Structure:**
      ${numSegments === 1 ? 
        '- Single 8-second segment: Complete micro-story with hook (2s), development (4s), conclusion (2s)' :
        numSegments === 2 ?
        '- Segment 1 (0-8s): Hook and setup\n- Segment 2 (8-16s): Development and conclusion' :
        '- Segment 1 (0-8s): Hook and initial setup\n- Segment 2 (8-16s): Main development and twist\n- Segment 3 (16-24s): Climax and conclusion'
      }

      **Output Format:**
      For each 8-second segment, provide:
      
      SEGMENT [number] (Time: [start]-[end]s)
      Scene: [scene number]
      Duration: 8s
      Visual: [Detailed visual description]
      Audio/Dialogue: [Script for voiceover/dialogue]
      Transition: [How this segment smoothly connects to the next, if applicable]

      ---
      IMPORTANT: Write ALL text, dialogue, and narration in English.
      
      EXAMPLE for a 16-second story:

      SEGMENT 1 (Time: 0-8s)
      Scene: 1
      Duration: 8s
      Visual: A chef enters a messy kitchen, looks shocked, then starts organizing ingredients rapidly.
      Audio/Dialogue: "30 minutes until dinner service! This kitchen is chaos... but watch this transformation!"
      Transition: Camera follows chef's hand reaching for the first pan...

      SEGMENT 2 (Time: 8-16s)
      Scene: 2
      Duration: 8s
      Visual: ...hand grabs pan, rapid montage of cooking, plating, final shot of perfect dishes.
      Audio/Dialogue: "Chop, sear, plate - years of training in action. From chaos to Michelin-star perfection!"
      Transition: [Final segment - no transition needed]

      ---

      Now generate the complete ${totalSeconds}-second story script with seamless 8-second segments.
    `;

    try {
      const model = this.genAI.getGenerativeModel({ model: this.config.model || "gemini-1.5-flash" });
      const result = await model.generateContent(prompt);
      const response = await result.response;
      const text = response.text();
      return text;
    } catch (error) {
      console.error("Error generating script from Gemini API:", error);
      if (error instanceof Error) {
        throw new Error(`Failed to generate script. ${error.message}`);
      }
      throw new Error("An unknown error occurred while generating the script.");
    }
  }

  /**
   * Generate a video from script using Veo API or fallback to storyboard
   */
  async generateVideo(script: string): Promise<string> {
    console.log("Video generation requested for segment");
    
    try {
      // Extract visual and audio descriptions from the script
      const visualMatch = script.match(/Visual:\s*([^\n]+)/i);
      const audioMatch = script.match(/Audio\/Dialogue:\s*([^\n]+)/i);
      const segmentMatch = script.match(/SEGMENT\s+(\d+)/i);
      
      if (!visualMatch) {
        console.warn("No visual description found in script");
        return this.generatePlaceholder(script);
      }
      
      const visualDescription = visualMatch[1].trim();
      const audioDescription = audioMatch ? audioMatch[1].trim() : "";
      const segmentNumber = segmentMatch ? segmentMatch[1] : "1";
      
      // Try to generate video with Veo API first
      if (this.veoAI) {
        try {
          console.log("Attempting to generate video with Veo API...");
          
          // Create a comprehensive prompt for Veo
          const veoPrompt = `
            Create an 8-second video for YouTube Shorts (9:16 aspect ratio).
            Visual scene: ${visualDescription}
            Audio/Narration: ${audioDescription}
            Style: Professional, vibrant, engaging, high quality
            Camera movement: Dynamic but smooth
            Lighting: Bright and clear
          `.trim();
          
          // Start video generation with Veo
          const operation = await this.veoAI.models.generateVideos({
            model: this.config.videoModel || 'veo-3.0-generate-preview',
            prompt: veoPrompt,
            config: {
              numberOfVideos: 1,
              aspectRatio: '9:16',  // YouTube Shorts format
              negativePrompt: 'blurry, low quality, distorted, amateur'
            }
          });
          
          console.log("Veo video generation started, operation ID:", operation.name);
          
          // Poll for completion (video generation takes time)
          let completed = false;
          let pollCount = 0;
          const maxPolls = 60; // Max 5 minutes (60 * 5 seconds)
          
          while (!completed && pollCount < maxPolls) {
            await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
            
            // Check operation status
            const status = await this.veoAI.operations.get(operation.name);
            
            if (status.done) {
              completed = true;
              
              if (status.response && status.response.generatedVideos && status.response.generatedVideos.length > 0) {
                const videoFile = status.response.generatedVideos[0].video;
                console.log("Veo video generated successfully:", videoFile);
                
                // Return the video file reference
                // In production, you'd download this and serve it properly
                return `veo:${videoFile}`;
              } else if (status.error) {
                console.error("Veo generation failed:", status.error);
                throw new Error(`Veo generation failed: ${status.error.message}`);
              }
            }
            
            pollCount++;
            console.log(`Polling Veo generation... (${pollCount}/${maxPolls})`);
          }
          
          if (!completed) {
            console.warn("Veo generation timed out, falling back to storyboard");
          }
          
        } catch (veoError) {
          console.error("Veo API error:", veoError);
          // Fall back to storyboard generation
        }
      }
      
      // Fallback: Generate detailed storyboard with image generation prompts using Gemini
      const storyboardPrompt = `
        You are a professional video director and AI image generation expert. Create a detailed 8-second video storyboard with 4 keyframes (one every 2 seconds).
        
        Scene Description:
        Visual: ${visualDescription}
        Audio/Dialogue: ${audioDescription}
        
        For each of the 4 keyframes, provide:
        1. Exact frame description (camera angle, composition, characters, objects, actions)
        2. Image generation prompt (detailed prompt for AI image generation, including style, lighting, colors)
        3. Color palette (main colors in hex format)
        4. Lighting description
        5. Motion/animation notes (how this frame transitions to the next)
        6. Sound effects at that moment
        
        IMPORTANT: Make the image prompts very detailed and suitable for AI image generation (like Stable Diffusion, DALL-E, or Imagen).
        
        Format your response as JSON with this structure:
        {
          "segment": ${segmentNumber},
          "duration": "8s",
          "frames": [
            {
              "time": "0s",
              "description": "...",
              "imagePrompt": "Detailed prompt for AI image generation...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            },
            {
              "time": "2s",
              "description": "...",
              "imagePrompt": "...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            },
            {
              "time": "4s",
              "description": "...",
              "imagePrompt": "...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            },
            {
              "time": "6s",
              "description": "...",
              "imagePrompt": "...",
              "colors": ["#hex1", "#hex2", "#hex3"],
              "lighting": "...",
              "motion": "...",
              "sound": "..."
            }
          ],
          "style": "YouTube Shorts, 9:16 aspect ratio, vibrant and engaging, photorealistic",
          "veoPrompt": "A single prompt for the entire 8-second video that could be used with Veo API"
        }
      `;
      
      try {
        const model = this.genAI.getGenerativeModel({ 
          model: "gemini-1.5-flash",
          generationConfig: {
            temperature: 0.7,
            topP: 0.8,
            maxOutputTokens: 3000,
            responseMimeType: "application/json"
          }
        });
        
        const result = await model.generateContent(storyboardPrompt);
        const response = await result.response;
        const storyboardText = response.text();
        
        // Try to parse the JSON response
        let storyboardData;
        try {
          // Try direct JSON parse first
          storyboardData = JSON.parse(storyboardText);
          console.log("Generated storyboard with image prompts:", JSON.stringify(storyboardData, null, 2).substring(0, 500) + "...");
          
          // Store the Veo prompt for future use when API becomes available
          if (storyboardData.veoPrompt) {
            console.log("Veo prompt generated:", storyboardData.veoPrompt);
          }
          
        } catch (parseError) {
          // Try to extract JSON from markdown code blocks
          const jsonMatch = storyboardText.match(/```json\n?([\s\S]*?)\n?```/) || 
                           storyboardText.match(/\{[\s\S]*\}/);
          
          if (jsonMatch) {
            const jsonStr = jsonMatch[1] || jsonMatch[0];
            storyboardData = JSON.parse(jsonStr);
          } else {
            console.warn("Could not parse storyboard JSON, using fallback");
            storyboardData = {
              segment: segmentNumber,
              description: storyboardText,
              visual: visualDescription,
              audio: audioDescription,
              frames: []
            };
          }
        }
        
        // Create an enhanced video placeholder with storyboard data
        return this.generateStoryboardVideo(storyboardData);
        
      } catch (error) {
        console.error("Error generating storyboard:", error);
        return this.generateEnhancedPlaceholder(visualDescription, audioDescription, "");
      }
      
    } catch (error) {
      console.error("Error in video generation:", error);
      return this.generatePlaceholder(script);
    }
  }
  
  /**
   * Generate a storyboard-based video placeholder
   */
  private generateStoryboardVideo(storyboardData: any): string {
    const timestamp = Date.now();
    const data = {
      type: "storyboard_video",
      segment: storyboardData.segment || 1,
      frames: storyboardData.frames || [],
      style: storyboardData.style || "YouTube Shorts",
      duration: storyboardData.duration || "8s",
      timestamp: timestamp
    };
    
    // Encode the storyboard data as base64
    const encodedData = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    return `data:video/storyboard;base64,${encodedData}`;
  }
  
  /**
   * Generate a basic placeholder for video
   */
  private generatePlaceholder(script: string): string {
    const timestamp = Date.now();
    const segmentHash = this.hashCode(script.substring(0, 100));
    return `data:video/placeholder;base64,${btoa(`video-${segmentHash}-${timestamp}`)}`;
  }
  
  /**
   * Generate an enhanced placeholder with description
   */
  private generateEnhancedPlaceholder(visual: string, audio: string, detailed: string): string {
    const timestamp = Date.now();
    const data = {
      type: "video_placeholder",
      visual: visual.substring(0, 100),
      audio: audio.substring(0, 100),
      detailed: detailed.substring(0, 200),
      timestamp: timestamp
    };
    return `data:video/placeholder;base64,${btoa(JSON.stringify(data))}`;
  }
  
  /**
   * Simple hash function for generating unique IDs
   */
  private hashCode(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Parse script into structured format
   */
  parseScript(scriptText: string): StoryShotScript {
    const scenes = [];
    const sceneRegex = /Scene:\s*(\d+)\s*Duration:\s*([^\n]+)\s*Visual:\s*([^\n]+)\s*Audio\/Dialogue:\s*([^\n]+)/gi;
    
    let match;
    while ((match = sceneRegex.exec(scriptText)) !== null) {
      scenes.push({
        scene: parseInt(match[1]),
        duration: match[2].trim(),
        visual: match[3].trim(),
        audio: match[4].trim()
      });
    }

    return {
      content: scriptText,
      scenes
    };
  }
}

// Export singleton factory
export function createStoryShot(apiKey: string): StoryShot {
  return new StoryShot({ apiKey });
}